export const numberFormat = (
  value: number,
  customFormat?: {
    changeDotTo: string;
    changeCommaTo: string;
  }
): string => {
  const output = value
    .toLocaleString('en-US', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    })
    .replace('.00', '');

  if (!!customFormat) {
    return output.replaceAll('.', customFormat.changeDotTo).replaceAll(',', customFormat.changeCommaTo);
  }

  return output;
};

export const numberFormatRound = (number: number): string => {
  return Math.round(number).toFixed(0);
};

export const thousandsMinimizedFormat = (inputValue: number) => {
  const thousand = 1000;
  const million = 1e6;

  if (inputValue < thousand) {
    return inputValue.toString();
  }

  if (inputValue >= thousand && inputValue < million) {
    return `${Math.round((inputValue * 10) / thousand) / 10}k`;
  }

  if (inputValue >= million) {
    return `${Math.round((inputValue * 10) / thousand) / 10}M`;
  }
};

/**
 * Formats RMV token values according to the guideline:
 * - Thousands separator: comma (,)
 * - Decimal separator: dot (.)
 * - Max 2 decimals, only when meaningful
 * - Hide .00 for whole numbers
 * - No unnecessary zero padding
 *
 * Examples:
 * - 1111111111.11 → "1,111,111,111.11"
 * - 1111111111 → "1,111,111,111"
 * - 1234.5 → "1,234.5"
 * - 1234.00 → "1,234"
 * 
 * Full article:
 * https://realitynft.atlassian.net/wiki/spaces/PN/pages/358547464/Number+Currency+Formatting
 * 
 */
export const formatRMVValue = (value: number): string => {
  // Round to max 2 decimals
  const rounded = Math.round(value * 100) / 100;

  // Format with thousands separators
  return rounded.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

/**
 * An RMV figure the user is about to be CHARGED, grouped for reading but never rounded.
 *
 * The two formatters above are display formatters and both stop at 2 decimals:
 * `numberFormat` pads to exactly two and drops them when whole, `formatRMVValue` rounds to
 * at most two. That is the right treatment for a balance, a reward or a stat — a number
 * nobody is going to hand to a wallet. It is the wrong treatment for a PRICE, and quietly
 * so, because the failure only appears on the prices nobody had authored yet.
 *
 * `numberFormat(0.999)` is "1". A shopper reads 1 RMV, presses Buy, and their wallet asks
 * for 0.999 — or, on the other side of the same rounding, for more than the tag said. That
 * is a price tag disagreeing with a payment prompt, which is the one thing a storefront may
 * never do; it stopped being hypothetical when VIP-shop prices went per unit, since a total
 * is now `units × per-unit` and lands wherever that lands (7 spins at 0.6 RMV is 4.2, and
 * the backend sends exactly that — see `IVipShopOffer.price`).
 *
 * So: every digit the value actually carries, and no others. 4.2 renders "4.2" rather than
 * `numberFormat`'s padded "4.20" — padding a price to a decimal place it does not have
 * invites the same "which of these two numbers is real" question from the opposite
 * direction. Whole totals, which is every price authored to date, are unchanged: 85000
 * renders "85,000" exactly as before.
 *
 * `maximumFractionDigits: 20` rather than `String(value)`: both print the double's shortest
 * round-trip digits, but `toLocaleString` also groups thousands and — the reason it is not
 * optional — never switches to exponent notation. `String(1e21)` is "1e+21", which is not a
 * price, and a shop is exactly where a number that large arrives by data-entry accident. 20
 * is the ceiling the Intl option is specified to accept, and it is above the 17 significant
 * digits a double can distinguish, so nothing is rounded off on the way.
 *
 * This does NOT make the figure exact — it makes it faithful. The value is already a JS
 * double by the time it gets here (`price` is a JSON number), so what this guarantees is
 * that the shopper sees the number the client was given. The number anyone is actually
 * charged is the wei figure on the reveal / the rail's `rmv_amount`, which travels as an
 * exact digit string and never passes through a float at all.
 */
export const formatExactRMV = (value: number): string =>
  value.toLocaleString('en-US', { maximumFractionDigits: 20 });

/** What a price tag shows when the server withheld the number. */
export const PRICE_WITHHELD = '—';

/**
 * An offer's price as a tag, including when there isn't one.
 *
 * `IVipShopOffer.price` is `null` for a caller who may not buy the offer — the server
 * treats a price as part of a members-only listing rather than a public advertisement,
 * and sends it only to a member the same payload marks `eligible`. Every kind behaves
 * this way; there is no offer type that quotes to everyone.
 *
 * A dash rather than hiding the row: the price sits in a fixed footer beside the RMV mark
 * and the action pill, and removing it reflows the card for exactly the members who are
 * already being told they cannot buy. It reads as "not for you" next to the `locked` pill
 * that says why, which is the honest pairing — a `0` or an empty string would read as a
 * price of zero or as a loading state.
 */
export const formatOfferPrice = (value: number | null): string =>
  value === null ? PRICE_WITHHELD : formatExactRMV(value);
